import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getAmenitiesService,
  addAmenityService,
  deleteAmenityService,
  getAmenityByIDService,
  updateAmenityService,
} from '../../services/amenity.service';
import { toast } from 'react-toastify';

const initialState = {
  amenitiesList: {
    totalCount: 0,
    totalPage: 0,
    amenitiesData: [],
  },
  amenityData: null,
};

export const getAmenities = createAsyncThunk(
  `AMENITIES`,
  async ({ page, limit, sortField, sortType, search }, { rejectWithValue }) => {
    try {
      const response = await getAmenitiesService(
        page,
        limit,
        sortField,
        sortType,
        search
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const addAmenity = createAsyncThunk(
  'ADD_AMENITY',
  async (body, { rejectWithValue }) => {
    try {
      const response = await addAmenityService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getAmenityById = createAsyncThunk(
  'GET_AMENITY',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAmenityByIDService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const updateAmenity = createAsyncThunk(
  `UPDATE_AMENITY`,
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await updateAmenityService(id, body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const deleteAmenity = createAsyncThunk(
  'DELETE_AMENITY',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteAmenityService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const amenitySlice = createSlice({
  name: 'amenitySlice',
  initialState,
  reducers: {
    resetAmenity: (state) => {
      state.amenityData = null;
    },
    resetAmenitiesList: (state) => {
      state.amenitiesList = {
        totalCount: 0,
        totalPage: 0,
        amenitiesData: [],
      };
    },
  },
  extraReducers: (builder) => {
    // Get Amenities
    builder
      .addCase(getAmenities.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, amenities } = payload?.data?.data;
          state.amenitiesList = {
            totalCount: count,
            totalPage,
            amenitiesData: [...amenities],
          };
        }
      })
      .addCase(getAmenities.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Add Amenity
    builder
      .addCase(addAmenity.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(addAmenity.rejected, (state, { payload }) => {
        throw payload;
      });

    // Get Amenity Details
    builder
      .addCase(getAmenityById.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.amenityData = payload?.data?.data;
        }
      })
      .addCase(getAmenityById.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Update Amenity
    builder
      .addCase(updateAmenity.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(updateAmenity.rejected, (_, { payload }) => {
        throw payload;
      });

    // Delete Amenity
    builder
      .addCase(deleteAmenity.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(deleteAmenity.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });
  },
});

export const { resetAmenity, resetAmenitiesList } = amenitySlice.actions;

export default amenitySlice.reducer;
